/**
 * Soporte de micrófono del navegador.
 *
 * Los navegadores embebidos de las apps (Gmail, LinkedIn, Instagram, Facebook,
 * Outlook) no exponen `navigator.mediaDevices`: ni iOS ni Android dan acceso al
 * micrófono fuera del navegador real. Tocar `navigator.mediaDevices.getUserMedia`
 * ahí lanza un TypeError que deja la aplicación entera sin montar — pantalla en
 * blanco, sin mensaje. Todo acceso a la API pasa por aquí.
 */

export const getMediaDevices = (): MediaDevices | null => {
  if (typeof navigator === "undefined") return null;

  const mediaDevices = navigator.mediaDevices;
  if (!mediaDevices || typeof mediaDevices.getUserMedia !== "function") {
    return null;
  }

  return mediaDevices;
};

export const isMediaSupported = (): boolean => getMediaDevices() !== null;

const IN_APP_BROWSERS: Array<{ name: string; pattern: RegExp }> = [
  { name: "Facebook", pattern: /FBAN|FBAV|FB_IAB/i },
  { name: "Instagram", pattern: /Instagram/i },
  { name: "LinkedIn", pattern: /LinkedInApp/i },
  { name: "Gmail", pattern: /GSA\/|Gmail/i },
  { name: "Outlook", pattern: /Outlook-(iOS|Android)/i },
  { name: "WhatsApp", pattern: /WhatsApp/i },
  { name: "Telegram", pattern: /Telegram/i },
  { name: "TikTok", pattern: /BytedanceWebview|musical_ly/i },
  { name: "Twitter", pattern: /Twitter/i },
  { name: "Snapchat", pattern: /Snapchat/i },
  // WebView genérico de Android
  { name: "WebView", pattern: /;\s?wv\)/i },
];

/**
 * Nombre de la app cuyo navegador embebido está abriendo la página, o null si
 * estamos en un navegador de verdad. Solo sirve para afinar el mensaje: la
 * decisión de bloquear la llamada la toma `isMediaSupported`.
 */
export const getInAppBrowserName = (): string | null => {
  if (typeof navigator === "undefined") return null;

  const ua = navigator.userAgent || "";
  const match = IN_APP_BROWSERS.find(({ pattern }) => pattern.test(ua));

  return match ? match.name : null;
};
